import * as React from "react";
import { Box, Stack, Typography, Skeleton, IconButton, Tooltip } from "@mui/material";
import AlertIcon from "../alerting/alert_icon";
import { timeAgo, toLocaleString } from "../../scripts/utils";
import { useDevice } from "../../hooks/use_site";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import NotificationsIcon from "@mui/icons-material/Notifications";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { AlertColors, AlertLevelClass, levelToClass } from "./alert_enums";

export function AlertOld({ alert, device }) {
  return (
    <>
      <Stack flexDirection="row" alignItems="center" py={1}>
        <AlertIcon level={alert.alert_level} icon={alert.icon} />
        <Typography variant="body" sx={{ ml: 1 }}>
          {device && alert.device_id ? `[${alert.device_id}] ` : null}
          {alert.info}
        </Typography>
      </Stack>
      <Typography variant="body2" ml={6}>
        {alert.additional_info}
      </Typography>
    </>
  );
}

export default function Alert({ alert, isLoading, onMute, onDeviceClick, isMuted = false }) {
  const device = useDevice(alert?.device_id);

  const handleMuteClick = (e) => {
    e.stopPropagation();
    if (onMute && alert) {
      onMute(alert);
    }
  };

  const getAlertColor = () => {
    if (!alert || !alert.alert_level) return AlertColors.INFO;
    const levelClass = levelToClass(alert.alert_level);
    switch (levelClass) {
      case AlertLevelClass.DEBUG:
        return AlertColors.DEBUG;
      case AlertLevelClass.LOG:
        return AlertColors.LOG;
      case AlertLevelClass.INFO:
        return AlertColors.INFO;
      case AlertLevelClass.WARNING:
        return AlertColors.WARNING;
      case AlertLevelClass.ERROR:
        return AlertColors.ERROR;
      case AlertLevelClass.CRITICAL:
        return AlertColors.CRITICAL;
      default:
        return AlertColors.INFO;
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        alignItems: "center",
        display: "flex",
        textAlign: "left",
        px: 1,
        py: 0.5,
        opacity: isMuted ? 0.5 : 1,
        backgroundColor: isMuted ? "rgba(0, 0, 0, 0.02)" : "transparent",
        "&:hover": {
          backgroundColor: isMuted ? "rgba(0, 0, 0, 0.04)" : "rgba(0, 0, 0, 0.02)",
        },
      }}
    >
      <Box
        sx={{
          minWidth: "30px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          alignSelf: "flex-start",
          marginTop: "5px",
        }}
      >
        {isLoading ? (
          <Skeleton variant="circular" width={30} height={30} />
        ) : (
          <AlertIcon level={alert.alert_level} icon={alert.icon} />
        )}
      </Box>

      <Stack sx={{ flexGrow: 1, marginLeft: 2 }}>
        <Typography variant="body2">{isLoading ? <Skeleton width="80%" /> : alert.info}</Typography>
        <Typography variant="caption">
          {isLoading ? (
            <Skeleton width="60%" />
          ) : (
            <>
              {device ? (
                onDeviceClick ? (
                  <Tooltip title="Go to device">
                    <Box
                      component="span"
                      onClick={() => onDeviceClick(alert.device_id)}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 700,
                        color: 'text.secondary',
                        '&:hover': { textDecoration: 'underline' },
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '2px',
                      }}
                    >
                      {device.name} <OpenInNewIcon sx={{ fontSize: 10 }} /> {"\u2022"}
                    </Box>
                  </Tooltip>
                ) : (
                  <b>{"\u2022"} {device.name} </b>
                )
              ) : null}{" "}
              {alert.additional_info}
            </>
          )}
        </Typography>
        <Typography variant="caption" color={"#aaa"} sx={{ textAlign: "left" }}>
          {isLoading ? (
            <Skeleton width="30%" />
          ) : alert.valid_from != null ? (
            timeAgo(new Date(1000 * alert.valid_from))
          ) : null}
        </Typography>
      </Stack>

      {!isLoading && onMute && (
        <Box sx={{ ml: 1 }}>
          <Tooltip title={isMuted ? "Manage mute settings" : "Mute this alert"}>
            <IconButton
              size="small"
              onClick={handleMuteClick}
              sx={{
                opacity: 0.6,
                "&:hover": { opacity: 1 },
                color: "#363636",
              }}
            >
              {isMuted ? <NotificationsIcon fontSize="small" /> : <NotificationsOffIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      )}
      </Box>
)}
